import { QueryClient, UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { graphql } from "../../../../generated/app-service-gql";
import {
  BidsQuery,
  BidsSimpleQuery,
  DeleteBidsMutation,
  DeleteBidsMutationVariables,
} from "../../../../generated/app-service-gql/graphql";
import { updateLeadQuery } from "../../leads/update_cache/updateLeadEntityCache";
import { useGraphQlMutation } from "../../useGraphQlClient";

const DELETE_BIDS_MUTATION = graphql(`
  mutation deleteBids($ids: [String!]!) {
    deleteBids(ids: $ids) {
      id
      lead {
        id
        name
      }
    }
  }
`);

export function useDeleteBids(
  options?: UseMutationOptions<DeleteBidsMutation, unknown, DeleteBidsMutationVariables, unknown>,
) {
  const queryClient = useQueryClient();

  const { data, ...rest } = useGraphQlMutation(DELETE_BIDS_MUTATION, {
    ...options,
    onSuccess: (data, variables, context) => {
      for (const id of variables.ids) {
        queryClient.removeQueries({
          queryKey: ["bid", id],
        });
      }

      // TODO: Check at some point that this would be ok when useInfiniteBids query is used
      const deletedIds = data.deleteBids.map((l) => l.id);
      removeDeletedBidsFromCache(queryClient, deletedIds);

      for (const bid of data.deleteBids) {
        const leadId = bid.lead?.id;
        if (leadId) {
          updateLeadQuery(queryClient, leadId, (prevData) => {
            if (!prevData) return prevData;
            return {
              ...prevData,
              bid: null,
            };
          });
        }
      }

      options?.onSuccess?.(data, variables, context);
    },
  });

  return { data, ...rest };
}

/**
 * Removes any bids in the cache assigned to any of the deleted bids
 * @param queryClient
 * @param deletedIds
 */
function removeDeletedBidsFromCache(queryClient: QueryClient, deletedIds: string[]) {
  const allBidsQueries = queryClient.getQueryCache().findAll({ queryKey: ["bids"] });

  for (const bidsQuery of allBidsQueries) {
    queryClient.setQueryData(bidsQuery.queryKey, (prevData: BidsQuery | undefined) => {
      if (!prevData) return prevData;
      const removedBids = prevData.bids.results.filter((o) => deletedIds.includes(o.id));
      const updatedResults = prevData.bids.results.filter((o) => !deletedIds.includes(o.id));
      const removedValue = removedBids.reduce((sum, bid) => sum + (bid.value ?? 0), 0);
      return {
        bids: {
          ...prevData.bids,
          results: updatedResults,
          total: prevData.bids.total - removedBids.length,
          valueSum: prevData.bids.valueSum - removedValue,
        },
      };
    });
  }
  const allBidsSimpleQueries = queryClient.getQueryCache().findAll({ queryKey: ["bidsSimple"] });
  for (const bidsQuery of allBidsSimpleQueries) {
    queryClient.setQueryData(bidsQuery.queryKey, (prevData: BidsSimpleQuery | undefined) => {
      if (!prevData) return prevData;
      const removedBids = prevData.bids.results.filter((o) => deletedIds.includes(o.id));
      const updatedResults = prevData.bids.results.filter((o) => !deletedIds.includes(o.id));
      return {
        bids: {
          ...prevData.bids,
          results: updatedResults,
          total: prevData.bids.total - removedBids.length,
        },
      };
    });
  }
}
